import React, { useMemo } from 'react';
import { Calendar, ChevronDown, ChevronUp, SortAsc } from 'lucide-react';
import type { Index, PromptRun } from '../../services/api';
import IndexGallery from '../IndexGallery';

const INITIAL_INDEX_COUNT = 10;

export type SearchScopeIndexSort = 'name' | 'date';

interface SearchScopeControlsProps {
  controlIdPrefix: string;
  indexes: Index[];
  runs: PromptRun[];
  selectedIndexId: string;
  selectedRunIds: string[] | null;
  isLoadingIndexes: boolean;
  isBusy?: boolean;
  showIndexDropdown: boolean;
  showRunDropdown: boolean;
  showAllIndexes: boolean;
  indexSortBy: SearchScopeIndexSort;
  indexSortAsc: boolean;
  onIndexDropdownChange: (isOpen: boolean) => void;
  onRunDropdownChange: (isOpen: boolean) => void;
  onShowAllIndexesChange: (showAll: boolean) => void;
  onIndexSortChange: (sortBy: SearchScopeIndexSort) => void;
  onIndexChange: (indexId: string) => void;
  onRunIdsChange: (runIds: string[] | null) => void;
  getRunSecondaryText: (run: PromptRun) => string;
  className?: string;
}

const SearchScopeControls: React.FC<SearchScopeControlsProps> = ({
  controlIdPrefix,
  indexes,
  runs,
  selectedIndexId,
  selectedRunIds,
  isLoadingIndexes,
  isBusy = false,
  showIndexDropdown,
  showRunDropdown,
  showAllIndexes,
  indexSortBy,
  indexSortAsc,
  onIndexDropdownChange,
  onRunDropdownChange,
  onShowAllIndexesChange,
  onIndexSortChange,
  onIndexChange,
  onRunIdsChange,
  getRunSecondaryText,
  className = '',
}) => {
  const sortedIndexes = useMemo(() => [...indexes].sort((left, right) => {
    const comparison = indexSortBy === 'name'
      ? left.name.localeCompare(right.name)
      : new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
    return indexSortAsc ? comparison : -comparison;
  }), [indexSortAsc, indexSortBy, indexes]);

  const visibleIndexes = showAllIndexes
    ? sortedIndexes
    : sortedIndexes.slice(0, INITIAL_INDEX_COUNT);
  const selectedIndexName = indexes.find((index) => index.index_id === selectedIndexId)?.name;
  const allRunsSelected = selectedRunIds === null || selectedRunIds.length === runs.length;
  const selectedRun = selectedRunIds?.length === 1
    ? runs.find((run) => run.run_id === selectedRunIds[0])
    : null;
  const isIndexMenuOpen = showIndexDropdown && !isBusy;
  const isRunMenuOpen = showRunDropdown && runs.length > 0 && !isBusy;
  const runSelectionLabel = !selectedIndexId
    ? 'Select an index first'
    : runs.length === 0
      ? 'No searchable runs available'
      : allRunsSelected
        ? 'All searchable runs'
        : selectedRun
          ? selectedRun.prompt_name
          : selectedRunIds?.length
            ? `${selectedRunIds.length} runs selected`
            : 'Select searchable runs';

  return (
    <div className={`search-scope-controls ${className}`.trim()}>
      <div className="form-group flex-1 search-scope-field">
        <label htmlFor={`${controlIdPrefix}-index-trigger`} className="form-label">
          Index
        </label>
        <div className="custom-select-container">
          <button
            id={`${controlIdPrefix}-index-trigger`}
            type="button"
            className="custom-select-trigger"
            onClick={() => {
              onRunDropdownChange(false);
              onIndexDropdownChange(!showIndexDropdown);
            }}
            disabled={isBusy || isLoadingIndexes || indexes.length === 0}
            aria-haspopup="true"
            aria-expanded={isIndexMenuOpen}
          >
            <span className="select-value">
              {isLoadingIndexes ? 'Loading indexes…' : selectedIndexName || 'Select an index'}
            </span>
            {isIndexMenuOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {isIndexMenuOpen && (
            <div className={`custom-dropdown search-fields-dropdown search-index-dropdown ${showAllIndexes ? 'show-all' : ''}`}>
              <div className="dropdown-header">
                <span className="dropdown-title">Select index</span>
              </div>
              <div className="dropdown-header search-scope-sort-header">
                <div className="sort-buttons">
                  <button
                    type="button"
                    className={`sort-btn ${indexSortBy === 'name' ? 'active' : ''}`}
                    onClick={() => onIndexSortChange('name')}
                    title={`Sort by name ${indexSortBy === 'name' ? (indexSortAsc ? '(A-Z)' : '(Z-A)') : ''}`}
                  >
                    <SortAsc
                      size={14}
                      style={{
                        transform: indexSortBy === 'name' && !indexSortAsc ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                    <span>Name</span>
                  </button>
                  <button
                    type="button"
                    className={`sort-btn ${indexSortBy === 'date' ? 'active' : ''}`}
                    onClick={() => onIndexSortChange('date')}
                    title={`Sort by date ${indexSortBy === 'date' ? (indexSortAsc ? '(Oldest first)' : '(Newest first)') : ''}`}
                  >
                    <Calendar size={14} />
                    <span>Date</span>
                    {indexSortBy === 'date' && (
                      <SortAsc
                        size={12}
                        style={{
                          transform: !indexSortAsc ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s ease',
                          marginLeft: '-4px',
                        }}
                      />
                    )}
                  </button>
                </div>
              </div>

              <div className={`dropdown-list search-fields-list ${showAllIndexes ? 'show-all' : ''}`}>
                <IndexGallery
                  indexes={visibleIndexes}
                  selectedIndexId={selectedIndexId}
                  isLoading={isLoadingIndexes}
                  disabled={isBusy || isLoadingIndexes}
                  onSelect={(indexId) => {
                    onIndexChange(indexId);
                    onIndexDropdownChange(false);
                  }}
                />
              </div>

              {indexes.length > INITIAL_INDEX_COUNT && (
                <div className="dropdown-footer">
                  <button
                    type="button"
                    className="show-more-btn"
                    onClick={() => onShowAllIndexesChange(!showAllIndexes)}
                  >
                    {showAllIndexes ? 'Show less' : `Show ${indexes.length - INITIAL_INDEX_COUNT} more`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="form-group flex-1 search-scope-field">
        <label htmlFor={`${controlIdPrefix}-run-trigger`} className="form-label">
          Searchable extraction runs
        </label>
        <div className="custom-select-container">
          <button
            id={`${controlIdPrefix}-run-trigger`}
            type="button"
            className="custom-select-trigger"
            onClick={() => {
              if (runs.length === 0) return;
              onIndexDropdownChange(false);
              onRunDropdownChange(!showRunDropdown);
            }}
            disabled={isBusy || !selectedIndexId || runs.length === 0}
            aria-haspopup="true"
            aria-expanded={isRunMenuOpen}
          >
            <span className="select-value">{runSelectionLabel}</span>
            {isRunMenuOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {isRunMenuOpen && (
            <div className="custom-dropdown search-fields-dropdown grouped-dropdown">
              <div className="dropdown-header">
                <span className="dropdown-title">Select extraction runs</span>
                <button
                  type="button"
                  className="clear-selection-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRunIdsChange(null);
                  }}
                >
                  All
                </button>
              </div>

              <div className="dropdown-list search-fields-list">
                {runs.map((run) => {
                  const isSelected = selectedRunIds === null || selectedRunIds.includes(run.run_id);
                  return (
                    <label
                      key={run.run_id}
                      className={`dropdown-item search-field-item ${isSelected ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          const availableIds = runs.map((candidate) => candidate.run_id);
                          const currentIds = selectedRunIds || availableIds;
                          const nextIds = isSelected
                            ? currentIds.filter((runId) => runId !== run.run_id)
                            : Array.from(new Set([...currentIds, run.run_id]));
                          const nextAvailableIds = nextIds.filter((runId) => availableIds.includes(runId));
                          onRunIdsChange(nextAvailableIds.length === availableIds.length ? null : nextAvailableIds);
                        }}
                      />
                      <span className="field-name">{run.prompt_name}</span>
                      <span className="field-type">{getRunSecondaryText(run)}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchScopeControls;
